/**
 * ScrollDatePicker — the date wheel from Figma (node 6873:27947): three
 * scrolling columns (day, month, year) over a highlighted centre band, with the
 * rows above and below fading into the surface.
 *
 * A birth date is decades away from today, so a month-by-month calendar grid is
 * the wrong instrument: every column here reaches its whole range in one
 * gesture. Each column is a scroll-snap list; whatever settles in the centre is
 * the value, and Arrow keys step it for keyboard users.
 */
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './scrollDatePicker.css';

/**
 * Row height in px. It must match `--space-12` in the stylesheet, because the
 * offset of the centred row is what translates a scroll position into a value.
 */
const ITEM_HEIGHT = 48;

/**
 * Blank rows above and below the options. The viewport is five rows tall, so two
 * spacers let the first and last option reach the centre band, and they make
 * `scrollTop === index * ITEM_HEIGHT` hold for every option.
 */
const PADDING_ROWS = 2;

/** How long the column waits for the scroll to stop before reading a value. */
const SETTLE_MS = 90;

const monthFormat = new Intl.DateTimeFormat(undefined, { month: 'long' });

/** The last day of the month `month` (0-11) falls in, leap years included. */
const daysInMonth = (year: number, month: number): number =>
  new Date(year, month + 1, 0).getDate();

const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Whole numbers from `from` to `to`, inclusive. */
const range = (from: number, to: number): number[] =>
  from > to ? [] : Array.from({ length: to - from + 1 }, (_, index) => from + index);

/**
 * Builds a real, in-range date from parts the columns cannot all constrain at
 * once: moving to a shorter month has to keep an existing day (31 Jan → Feb),
 * and the boundary years still have to respect the exact `min`/`max` day.
 */
export const resolveDate = (
  year: number,
  month: number,
  day: number,
  min: Date,
  max: Date,
): Date => {
  const boundedMonth = clampNumber(month, 0, 11);
  const boundedDay = clampNumber(day, 1, daysInMonth(year, boundedMonth));
  const candidate = new Date(year, boundedMonth, boundedDay);
  if (candidate < min) return new Date(min);
  if (candidate > max) return new Date(max);
  return candidate;
};

interface ScrollColumnProps {
  /** Selectable values, ascending. */
  values: number[];
  /** The value currently centred. */
  selected: number;
  onSelect: (value: number) => void;
  /** Renders a value; the year column shows it verbatim, months as names. */
  format: (value: number) => string;
  label: string;
}

function ScrollColumn({ values, selected, onSelect, format, label }: ScrollColumnProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const columnId = useId();

  const selectedIndex = Math.max(
    0,
    values.findIndex(value => value === selected),
  );

  /**
   * Focus stays on the column, so the active option has to be named rather than
   * focused: without this a screen reader announces nothing as the wheel turns.
   */
  const optionId = (value: number): string => `${columnId}-${value}`;
  const activeValue = values[selectedIndex];

  /**
   * Follows the value: the wheel opens on the current date, and a month that
   * clamped the day (31 Jan → Feb) or a trimmed boundary year re-centres the
   * column on the value it kept.
   */
  useEffect(() => {
    listRef.current?.scrollTo({ top: selectedIndex * ITEM_HEIGHT });
  }, [selectedIndex, values]);

  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  /**
   * Reads the centred row once the scroll stops. Scroll-snap owns the final
   * resting position, so a settled row that is already selected needs nothing.
   */
  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      const index = clampNumber(
        Math.round(list.scrollTop / ITEM_HEIGHT),
        0,
        values.length - 1,
      );
      const value = values[index];
      if (value !== undefined && value !== selected) onSelect(value);
    }, SETTLE_MS);
  };

  const step = (offset: number) => {
    const next = values[clampNumber(selectedIndex + offset, 0, values.length - 1)];
    if (next !== undefined && next !== selected) onSelect(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      step(-values.length);
    } else if (event.key === 'End') {
      event.preventDefault();
      step(values.length);
    }
  };

  const spacers = Array.from({ length: PADDING_ROWS }, (_, index) => index);

  return (
    <div
      ref={listRef}
      className="scroll-date-picker__column"
      role="listbox"
      aria-label={label}
      aria-activedescendant={activeValue === undefined ? undefined : optionId(activeValue)}
      tabIndex={0}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
    >
      {spacers.map(index => (
        <div key={`lead-${index}`} className="scroll-date-picker__spacer" aria-hidden />
      ))}
      {values.map(value => {
        const isSelected = value === selected;
        return (
          <div
            key={value}
            id={optionId(value)}
            className={`scroll-date-picker__option${isSelected ? ' scroll-date-picker__option--selected' : ''}`}
            role="option"
            aria-selected={isSelected}
            onClick={() => { if (!isSelected) onSelect(value); }}
          >
            {format(value)}
          </div>
        );
      })}
      {spacers.map(index => (
        <div key={`trail-${index}`} className="scroll-date-picker__spacer" aria-hidden />
      ))}
    </div>
  );
}

export interface ScrollDatePickerProps {
  /** The selected date. */
  value: Date;
  /** Receives the new date, already clamped into `min`/`max`. */
  onChange: (date: Date) => void;
  /** Earliest selectable date, inclusive. */
  min: Date;
  /** Latest selectable date, inclusive. */
  max: Date;
  /** Names the whole wheel, e.g. "Date of birth". */
  ariaLabel: string;
}

export function ScrollDatePicker({ value, onChange, min, max, ariaLabel }: ScrollDatePickerProps) {
  const { t } = useTranslation();

  const year = value.getFullYear();
  const month = value.getMonth();
  const day = value.getDate();

  /**
   * Only the boundary years are partly out of range, so the month and day
   * columns are trimmed there and offer everything in between.
   */
  const years = useMemo(
    () => range(min.getFullYear(), max.getFullYear()),
    [min, max],
  );

  const months = useMemo(
    () =>
      range(
        year === min.getFullYear() ? min.getMonth() : 0,
        year === max.getFullYear() ? max.getMonth() : 11,
      ),
    [year, min, max],
  );

  const days = useMemo(
    () =>
      range(
        year === min.getFullYear() && month === min.getMonth() ? min.getDate() : 1,
        year === max.getFullYear() && month === max.getMonth()
          ? Math.min(max.getDate(), daysInMonth(year, month))
          : daysInMonth(year, month),
      ),
    [year, month, min, max],
  );

  const commit = useCallback(
    (nextYear: number, nextMonth: number, nextDay: number) => {
      onChange(resolveDate(nextYear, nextMonth, nextDay, min, max));
    },
    [onChange, min, max],
  );

  return (
    <div className="scroll-date-picker" role="group" aria-label={ariaLabel}>
      <span className="scroll-date-picker__band" aria-hidden />
      <div className="scroll-date-picker__columns">
        <ScrollColumn
          values={days}
          selected={day}
          onSelect={next => commit(year, month, next)}
          format={String}
          label={t('common.accessibility.dayColumn')}
        />
        <span className="scroll-date-picker__divider" aria-hidden />
        <ScrollColumn
          values={months}
          selected={month}
          onSelect={next => commit(year, next, day)}
          format={index => monthFormat.format(new Date(2023, index, 1))}
          label={t('common.accessibility.monthColumn')}
        />
        <span className="scroll-date-picker__divider" aria-hidden />
        <ScrollColumn
          values={years}
          selected={year}
          onSelect={next => commit(next, month, day)}
          format={String}
          label={t('common.accessibility.yearColumn')}
        />
      </div>
      <span className="scroll-date-picker__fade scroll-date-picker__fade--top" aria-hidden />
      <span className="scroll-date-picker__fade scroll-date-picker__fade--bottom" aria-hidden />
    </div>
  );
}
